/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { GetStaticPaths } from "next";
import { GetStaticProps } from "next";
import { NotFoundPage } from "pages/404";
import React from "react";
import { SanityPage } from "gui/sanity/page";
import { SanityProps } from "gui/sanity/page";
import UserPage, {
  Props as UserPageProps,
  fetchProps as fetchUserPageProps,
} from "gui/UserPage/UserPage";
import { fetchPage } from "lib/sanity/pages";
import { fetchPages } from "lib/sanity/pages";
import TeamPage, {
  Props as TeamPageProps,
  fetchProps as fetchTeamProps,
} from "gui/TeamPage/TeamPage";

// This route serves multiple page types:
//
// * Static pages from the Sanity CMS for paths like `/pricing`.
// * User pages for plain usernames like `/username`.
//
type Props = SanityProps | UserPageProps | TeamPageProps;

export const Page: React.FC<Props> = (props) => {
  if ("sanity" in props) {
    return <SanityPage {...props} sanity={props.sanity} />;
  } else if ("user" in props) {
    return <UserPage {...props} />;
  } else if ("team" in props) {
    return <TeamPage {...props} />;
  } else {
    return <NotFoundPage />;
  }
};

// All static pages from the CMS are generated at build time.
// All other user/dashboard pages that share this route are
// generated runtime using the fallback feature.
export const getStaticPaths: GetStaticPaths = async () => {
  const items = await fetchPages(false);

  const paths = items
    // TODO: remove when we have migrated to new pricing pages.
    .filter(({ slug }) => slug.current !== "pricing")
    .map((item) => {
      return { params: { slug: item.slug.current } };
    });

  return {
    paths,
    fallback: "blocking",
  };
};

// Fetch page props for the matching page type.
export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const slug = ctx.params?.slug as string;

  // ** Sanity page **
  // Serve any static content page that matches the slug.
  // Content pages do not need revalidation since the CMS triggers rebuilds.
  const sanity = await fetchPage(slug, Boolean(ctx.preview));
  if (sanity) {
    return {
      props: {
        sanity,
        preview: Boolean(ctx.preview),
        timestamp: new Date().toISOString(),
      },
      revalidate: undefined, // Permanent cache
    };
  }

  // ** User page **
  // If there was no matching content page:
  // Serve a user page (if slug matches a username).
  const userPageProps = await fetchUserPageProps(slug);
  if (userPageProps) {
    return {
      props: {
        ...userPageProps,
        preview: Boolean(ctx.preview),
        timestamp: new Date().toISOString(),
      },
      revalidate: 3 * 60, // 3 minute cache
    };
  }

  // ** Team page **
  // Serve a team page (if slug matches a team handle).
  const teamPageProps = await fetchTeamProps(slug);
  if (teamPageProps) {
    return {
      props: {
        ...teamPageProps,
        preview: Boolean(ctx.preview),
        timestamp: new Date().toISOString(),
      },
      revalidate: 3 * 60, // 3 minute cache
    };
  }

  // ** 404 **
  // User does not exist. Don't cache that for long.
  // If someone checks if username is free, then makes user,
  // should not be stuck showing 404 page.
  return {
    props: {
      preview: Boolean(ctx.preview),
      timestamp: new Date().toISOString(),
    },
    revalidate: 10, // 10 second cache
  };
};

export default Page;
